import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Plus, Users, AlertCircle, Loader2, X } from "lucide-react";
import { ClassCard } from "../components/ui/school/class-management/class-card";
import { SchoolLayout } from "../components/ui/school/SchoolLayout";
import { listClasses, createClass } from "../hooks/api/schoolApi";

function CreateClassModal({ onClose, onSave }) {
  const [name, setName] = useState("");
  const [level, setLevel] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createClass({ name: name.trim(), level: level.trim() || name.trim() });
      onSave();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Create Class</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Class name *</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. SS2A or Year 11 Science"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Level / stream (optional)</label>
          <input value={level} onChange={e => setLevel(e.target.value)}
            placeholder="e.g. SS2"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div className="flex gap-3 pt-1">
          <button onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2.5 rounded-xl hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={!name.trim() || saving}
            className="flex-1 bg-teal-600 text-white font-bold py-2.5 rounded-xl hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Class
          </button>
        </div>
      </div>
    </div>
  );
}

const ClassManagementPage = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    listClasses()
      .then((data) => {
        setClasses(
          data.map((c) => ({
            ...c,
            id: c._id,
            classId: c._id,
            name: c.name ?? c.level,
            students: c.studentIds?.length ?? c.students ?? 0,
            avgScore: c.avgScore ?? 0,
            engagement: c.engagement ?? 0,
            progress: c.progress ?? 0,
            term: "Current Term",
            weeksToNextTerm: 4,
            status: "Active",
            subjects: c.subjects ?? [],
            recentActivities: [],
            borderColor: "primary",
            topPerformer: "—",
            needsSupport: "—",
            excellentCount: 0,
            strugglingCount: 0,
            avgSessions: 0,
            icon: "users",
          }))
        );
      })
      .catch((err) => {
        setError(err?.response?.data?.error?.message ?? "Could not load classes. Check your connection.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleViewStudents = (classId) => navigate(`/class-management/${classId}`);

  return (
    <SchoolLayout>
      <div className="p-4 md:p-8 overflow-y-auto">
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">Class Management</h1>
            <p className="text-gray-500 mt-1">Monitor academic progress and engagement across all classes.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-5 py-2.5 rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Class
          </button>
        </header>

        {loading && (
          <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading classes…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 max-w-lg">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {!loading && !error && classes.length === 0 && (
          <div className="text-center py-20 space-y-3">
            <Users className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="font-bold text-slate-700">No classes yet</p>
            <p className="text-slate-400 text-sm">Create your first class to start organising students.</p>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-teal-600 text-white font-bold px-5 py-2.5 rounded-xl hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" />
              Create Class
            </button>
          </div>
        )}

        {!loading && !error && classes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {classes.map((classData) => (
              <ClassCard
                key={classData._id}
                classData={classData}
                onViewStudents={handleViewStudents}
              />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateClassModal
          onClose={() => setShowCreate(false)}
          onSave={() => { setShowCreate(false); load(); }}
        />
      )}
    </SchoolLayout>
  );
};

export default ClassManagementPage;
